import * as d3 from 'd3';
import {colourObj} from "../data/colours";
import {MutableRefObject} from "react";
import {NumberObject, NumberObjectArray} from "../models/visualisation-data.interface";
import {HierarchyCircularNode, QuadtreeLeaf} from "d3";

interface BubbleChartProps {
    svgRef: MutableRefObject<SVGSVGElement | null>,
    dataGroup: NumberObjectArray[],
    width: number,
    height: number,
}

export function bubbleChart({svgRef, dataGroup, width, height}: BubbleChartProps) {
    if (dataGroup.length === 0) { // If dataGroup is empty, return an empty object
        return {};
    }

    const bubbleData = mapDataToBubbles(dataGroup[1]);

    const nodes = pack(bubbleData, width, height).leaves() as HierarchyCircularNode<NumberObject>[];

    const simulation = d3.forceSimulation(nodes)
        .force("x", d3.forceX(width / 2).strength(0.01))
        .force("y", d3.forceY(height / 2).strength(0.01))
        .force("cluster", forceCluster())
        .force("collide", forceCollide());

    const svg = d3.select(svgRef.current);

    svg.selectAll(".dot").remove();

    const node = svg.append("g")
        .attr("class", "dot")
        .selectAll("circle")
        .data(nodes)
        .join("circle")
        .attr("cx", d => numberToTwoDecimalPlaces(d.x))
        .attr("cy", d => numberToTwoDecimalPlaces(d.y))
        .attr("fill", d => colourObj[d.data.group])
        .attr("class", d => d.data.group)
        .call(drag(simulation) as any);

    if (dataGroup.length) {
        node.transition()
            .duration(150)
            .attrTween("r", (d): any => {
                const i = d3.interpolate(0, d.r);
                return (t: number) => d.r = i(t);
            });
    }

    if (dataGroup.length) {
        simulation.on("tick", () => {
            return node
                .attr("cx", d => numberToTwoDecimalPlaces(d.x))
                .attr("cy", d => numberToTwoDecimalPlaces(d.y));
        });
    }

    svg.node();

    return {simulation};
}

export function mapDataToBubbles(group: NumberObjectArray) {
    const dataArray = group.map(obj => {
        const [key, val] = Object.entries(obj).flat();
        return Array.from({length: val} as {length: number}, () => ({
            group: key, value: 1,
        }))
    })

    return ({
        children: Array.from(d3.group(dataArray.flat(), d => d.group), ([, children]) => ({children}))
    });
}

export function numberToTwoDecimalPlaces(num: number): number {
    return Math.round(num * 100) / 100;
}

export function forceCluster() {
    const strength = 0.2;
    let nodes: any[];

    function force(alpha: any) {
        const centroids = d3.rollup(nodes, centroid, d => d.data.group);
        const l = alpha * strength;
        for (const d of nodes) {
            const {x: cx, y: cy} = centroids.get(d.data.group) || {x: 0, y: 0};
            d.vx -= (d.x - cx) * l;
            d.vy -= (d.y - cy) * l;
        }
    }

    force.initialize = (n: any[]) => nodes = n;

    return force;
}

export function forceCollide() {
    const alpha = 0.4; // fixed for greater rigidity!
    const padding1 = 2; // separation between same-color nodes
    const padding2 = 30; // separation between different-color nodes
    let nodes: any[];
    let maxRadius: string | number;

    function force() {
        const quadtree = d3.quadtree(nodes, d => d.x, d => d.y);
        for (const d of nodes) {
            const r = d.r + maxRadius;
            const nx1 = d.x - r, ny1 = d.y - r;
            const nx2 = d.x + r, ny2 = d.y + r;

            quadtree.visit((q, x1, y1, x2, y2) => {
                if (!q.length) {
                    do {
                        if (q.data !== d) {
                            const r = d.r + q.data.r + (d.data.group === q.data.data.group ? padding1 : padding2);
                            let x = d.x - q.data.x, y = d.y - q.data.y, l = Math.hypot(x, y);
                            if (l < r) {
                                l = (l - r) / l * alpha;
                                d.x -= x *= l;
                                d.y -= y *= l;
                                q.data.x += x;
                                q.data.y += y;
                            }
                        }
                        q = q.next as QuadtreeLeaf<any>;
                    } while (Boolean(q));
                }


                return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
            });
        }
    }

    force.initialize = (n: any) => maxRadius = d3.max(nodes = n, (d: any) => d.r) || 0 + Math.max(padding1, padding2);

    return force;
}

export const pack = (data: any, width: number, height: number) => d3.pack()
    .size([width, height])
    .padding(1)(d3.hierarchy(data)
        .sum(d => d.value))

export function centroid(nodes: any[]) {
    let x = 0;
    let y = 0;
    let z = 0;
    for (const d of nodes) {
        let k = d.r ** 2;
        x += d.x * k;
        y += d.y * k;
        z += k;
    }
    return {x: x / z, y: y / z};
}

export const drag = (simulation: any) => {
    function dragstarted(event: any, d: any) {
        if (!event.active) {
            simulation.alphaTarget(0.3).restart();
        }
        d.fx = d.x;
        d.fy = d.y;
    }

    function dragged(event: any, d: any) {
        d.fx = event.x;
        d.fy = event.y;
    }

    function dragended(event: any, d: any) {
        if (!event.active) {
            simulation.alphaTarget(0);
        }
        d.fx = null;
        d.fy = null;
    }

    return d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
}
